import * as React from 'react';
import { connect } from 'react-redux';
import {
  PagesState, TabType, tabFocusSelf, isPageableTabInfo, isEnableGoToReference,
} from '../../reducers/pages';
import ToolIconButton from '../common/ToolIconButton';
import { AppStates, PreferenceState, Publication, getDefaultRendition } from '../../../../..';
import { setContentFeatureFlag } from '../../actions/PreferenceAction';
import { Action, Dispatch } from 'redux';
import { goToReference } from '../../actions/PageAction';
import { TableOfContents } from '../toc/TableOfContents';
import imagePastingTest from '../../debug/imagePastingTest';
import objectDrawingTest from '../../debug/objectDrawingTest';
import { ToolbarDirection, setToolbarDirection } from '../../actions/UIModeAction';
import { UIModeState } from '../../reducers/uimode';
import { getReflowableRendition } from '../../utils/epub';
import { ContentFeatureDefinition } from '../../types/epub';
import { getBookmarkReferenceIfPageableTab } from '../../helpers/pages';
import { BookmarkInfo } from '../../bookmark';
import { BookmarkItemListPopup, BookmarkItem } from '../bookmark/BookmarkItemListPopup';
import {
  MultiroleLinkItemListPopup, MultiroleLinkItem,
} from '../multirole-link/MultiroleLinkItemListPopup';
import { appendBookmark, deleteBookmark } from '../../bookmark/actions/BookmarkAction';
import { BookmarksState } from '../../bookmark/reducers/bookmark';
import { MultiroleLinkInfo } from '../../../../../common/src/ts/types/multiroleLink';
import { removeMultiroleLink } from '../../drawing/actions/DrawingAction';
import DrawingToolAction from '../../drawing/actions/DrawingToolAction';
import PageList from '../page-list/PageList';
import MiscSetting from '../misc-setting/MiscSetting';
import { SystemInformation } from '../system-information/SystemInformation';
import { PLATFORM } from '../../../../../common/src/ts/defines';
import { zIndex } from '../../constants';
import { generateAndCopyPartialWorkspaceURL } from '../../userdata/partial';
import {
  isFullscreen,
  addFullscreenEventHandler,
  removeFullscreenEventHandler,
} from '../../../../../common/src/ts/utils/fullscreen';
import { DrawingToolState } from '../../drawing/reducers/drawingTool';
import { CurrentTool, UIState } from '../../reducers/ui/ui';

// For UI Develop
import { setLegacyUI } from '../../actions/AppAction';
import SettingPopup from '../common/SettingPopup';
import { ItemList } from '../common/ItemList';
import UIAction from '../../actions/ui/UIAction';

const HelpView: React.SFC = () => {
  const style: React.CSSProperties = {
    position: 'fixed',
    left: '0',
    top: '0',
    right: '0',
    bottom: '96px',
    zIndex: zIndex.helpView,
    background: 'rgba(0, 0, 0, 0.4)',
    color: 'white',
    fontSize: '36px',
    textAlign: 'center',
    paddingTop: '100px',
  };

  return (
    <div style={style}>
      ヘルプ<br />
      アイコン一覧とその説明を記載
    </div>
  );
};

interface TransparencyItem {
  title: string;
  alpha: number;
}

const trasparencyItems: ReadonlyArray<TransparencyItem> = [
  { alpha: 0.9, title: '透明度10%' },
  { alpha: 0.8, title: '透明度20%' },
  { alpha: 0.7, title: '透明度30%' },
  { alpha: 0.6, title: '透明度40%' },
  { alpha: 0.5, title: '透明度50%' },
  { alpha: 0.4, title: '透明度60%' },
  { alpha: 0.3, title: '透明度70%' },
  { alpha: 0.2, title: '透明度80%' },
  { alpha: 0.1, title: '透明度90%' },
];

interface ToolboxPopupProps {
  readonly visible: boolean;
}

const ToolboxPopup: React.SFC<ToolboxPopupProps> = (props) => {
  if (!props.visible) {
    return null;
  }

  const style: React.CSSProperties = {
    position: 'absolute',
    right: '10px',
    bottom: '64px',
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    zIndex: zIndex.bottomToolbarToolboxPopup, // PageNavigatorより手前
  };

  return (
    <div style={style}>
      {props.children}
    </div>
  );
};

interface ToolboxProps {
  readonly onBackToBookshelf: () => void;
  readonly onSaveWorkspace: () => void;
  readonly onToggleFullScreen: () => void;
  readonly contentFeatures: { [featureID: string]: boolean };
  readonly dispatch: Dispatch<Action>;
  readonly publication: Publication | undefined;
  readonly preference: PreferenceState;
  readonly pagesState: PagesState;
  readonly uimode: UIModeState;
  readonly bookmarks: BookmarksState;
  readonly multiroleLinks: MultiroleLinkInfo[];
  readonly developMode: boolean;
  readonly legacyUI: boolean;
  readonly drawingTool: DrawingToolState;
  readonly ui: UIState;
}

interface ToolboxState {
  readonly showPageList: boolean;
  readonly showHelp: boolean;
  readonly showAppearancePopup: boolean;
  readonly showRemainingPopup: boolean;
  readonly showDevPopup: boolean;
  readonly showTableOfContents: boolean;
  readonly showBookmarksPopup: boolean;
  readonly showMultiroleLinksPopup: boolean;
  readonly showTransparencyPopup: boolean;
  readonly showMiscSetting: boolean;
  readonly showSystemInformation: boolean;
  readonly isFullscreen: boolean;
}

const objectTools: CurrentTool[] = [
  CurrentTool.MultiroleLink,
  CurrentTool.ImagePasting,
  CurrentTool.ObjectDrawing,
  CurrentTool.ObjectSelection,
  CurrentTool.ObjectRemoval,
  CurrentTool.AllErasing,
  CurrentTool.Textbox,
  CurrentTool.FreeHandPen,
  CurrentTool.Handerasing,
];

class Toolbox extends React.PureComponent<ToolboxProps, ToolboxState> {
  public constructor(props: ToolboxProps) {
    super(props);
    this.state = {
      showPageList: false,
      showHelp: false,
      showAppearancePopup: false,
      showRemainingPopup: false,
      showDevPopup: false,
      showTableOfContents: false,
      showBookmarksPopup: false,
      showMultiroleLinksPopup: false,
      showTransparencyPopup: false,
      showMiscSetting: false,
      showSystemInformation: false,
      isFullscreen: isFullscreen(),
    };
  }

  public componentDidMount() {
    addFullscreenEventHandler(this.onFullScreenChange);
  }

  public componentWillUnmount() {
    removeFullscreenEventHandler(this.onFullScreenChange);
  }

  private onFullScreenChange = () => {
    this.setState({ isFullscreen: isFullscreen() });
  }

  private handleToggleFullscreen = () => {
    this.props.onToggleFullScreen();
  }

  private handleToggleMenu<K extends keyof ToolboxState>(key: K) {
    if (key === 'showHelp' && !this.props.developMode) {
      // WIP
      alert('ヘルプ機能 (準備中)\n超教科書ビューアの利用方法について説明します。');
      return;
    }

    const newState: ToolboxState = {
      ...this.state,
      showPageList: false,
      showAppearancePopup: false,
      showDevPopup: false,
      showHelp: false,
      showRemainingPopup: false,
      showTableOfContents: false,
    };
    newState[key] = !(this.state[key] as boolean);
    this.setState(newState);
  }

  private handleToggleToolbarDirection = () => {
    const { VerticalLeft, Horizontal, VerticalRight } = ToolbarDirection;
    const { toolbarDirection } = this.props.uimode;
    const newDirection: ToolbarDirection = (() => {
      switch (toolbarDirection) {
        case VerticalLeft:
          return Horizontal;
        case Horizontal:
          return VerticalRight;
        case VerticalRight:
          return VerticalLeft;
      }
    })();
    setToolbarDirection(newDirection, this.props.dispatch);
  }

  private handleClickBookmarkButton = (reference: string, id?: string) => {
    if (!this.props.developMode) {
      // WIP
      const msg = [
        'しおり機能 (準備中)\n',
        '教科書紙面やコンテンツについて、ページ情報のみではなく書込み情報も含めて',
        '「しおり」を付けて保存し、いつでも復元できます。\n',
        'また、「しおり」を付けた書込み情報をファイルとして出力し、',
        'オフライン環境で他の人と共有することもできます（インポート・エクスポート機能）。',
      ].join('');
      alert(msg);
      return;
    }

    const { tabs, activeTab } = this.props.pagesState;
    const tab = tabs[activeTab];
    if (!isPageableTabInfo(tab)) {
      return;
    }

    if (id !== undefined) {
      this.props.dispatch(deleteBookmark(id));
      return;
    }
    this.props.dispatch(appendBookmark({
      reference,
      title: `${tab.currentPage + 1} ページ のしおり`,
      isImported: false,
      createdBy: this.props.preference.userName,
    }));
  }

  private handleClickBookmarkListItem = (
    item: BookmarkItem, _index: number,
  ) => {
    // TODO 未実装: ワンクッション挟んでから飛ぶ
    if (this.goToReferenceIfIsEnabled(item.reference)) {
      this.setState({ showBookmarksPopup: false });
    }
  }

  private goToReferenceIfIsEnabled(reference: string) {
    const { publication } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }
    const { tabs, activeTab } = this.props.pagesState;
    if (isEnableGoToReference(reference, tabs[activeTab], publication)) {
      this.props.dispatch(goToReference(reference));
      return true;
    }
    return false;
  }

  private handleClickMultiroleLinkListItem = (
    item: MultiroleLinkItem, _index: number,
  ) => {
    const { publication } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }
    const { tabs, activeTab } = this.props.pagesState;
    if (isEnableGoToReference(item.reference, tabs[activeTab], publication)) {
      this.props.dispatch(goToReference(item.reference));
      this.setState({ showMultiroleLinksPopup: false });
    }
  }

  private handleClickTransparencyListItem = (
    item: TransparencyItem, _index: number,
  ) => {
    this.props.dispatch(DrawingToolAction.setAlpha(item.alpha));
    this.setState({ showTransparencyPopup: false });
  }

  // For stress test
  private handleImagePastingTest = (count: number, size: 'large' | 'small') => {
    const { dispatch, publication } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }
    const numOfPages = publication.renditions[0].linearItems.length;
    const isLarge = (size === 'large');
    const width = isLarge ? 1920 : 256;
    const height = isLarge ? 1080 : 256;
    const mode = isLarge ? 'fill' : 'handwriting';
    imagePastingTest({ dispatch, numOfPages, count, width, height, mode });
  }

  // For stress test
  private handleObjectDrawingTest = (count: number) => {
    const { dispatch, publication } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }
    const numOfPages = publication.renditions[0].linearItems.length;
    objectDrawingTest({ dispatch, numOfPages, count });
  }

  // ブラウザビルド用
  private copyPartialWorkspaceURL = () => {
    generateAndCopyPartialWorkspaceURL({
      publicationId: this.props.publication!.id,
      pages: this.props.pagesState,
    });
  }

  // FIXME : 似たような処理（現在表示中のitemを取得）が色々なところに分散コピペされている
  private getContentFeatureIDsForCurrentPage(): ReadonlyArray<string> {
    const { publication, pagesState } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }
    const tab = pagesState.tabs[pagesState.activeTab];
    if (tab.focus !== tabFocusSelf) {
      const popup = pagesState.popups[tab.focus];
      return getDefaultRendition(publication).itemIndices[popup.path].features;
    }

    switch (tab.type) {
      case TabType.LinearPages:
        return getDefaultRendition(publication).linearItems[tab.currentPage].features;
      case TabType.ExtraPage:
        return getDefaultRendition(publication).itemIndices[tab.path].features;
      case TabType.ReflowablePages:
        return getReflowableRendition(publication).linearItems[tab.currentPage].features;
      case TabType.GeneratedContent:
        return [];
    }
  }

  private getContentFeatureDefinitionsForCurrentPage(): ContentFeatureDefinition[] {
    const { publication } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }

    const ids = this.getContentFeatureIDsForCurrentPage();
    return publication.featureDefinitions.filter(d => ids.indexOf(d.id) !== -1);
  }

  private createFeatureButtons() {
    const { dispatch, contentFeatures } = this.props;
    const definitions = this.getContentFeatureDefinitionsForCurrentPage();
    return definitions.map((definition) => {
      const enabled = contentFeatures[definition.id];
      return (
        <ToolIconButton
          key={definition.id}
          active={enabled}
          onClick={() => dispatch(setContentFeatureFlag(definition.id, !enabled))}
          text={definition.name}
          title={definition.name}
          ariaLabel={definition.id} />
      );
    });
  }

  public render() {
    const { pagesState, publication, bookmarks, developMode, legacyUI } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }

    let bookmarkID: string | undefined;
    const reference = getBookmarkReferenceIfPageableTab(pagesState, publication);
    if (reference !== undefined) {
      for (const bookmark of bookmarks) {
        if (bookmark.reference === reference) {
          bookmarkID = bookmark.id;
          break;
        }
      }
    }

    const wrapperStyle = {
      display: 'flex',
      flexShrink: 0,
    };

    return <>
      <div style={wrapperStyle}>
        <ToolIconButton
          onClick={() => this.handleToggleMenu('showPageList')}
          active={this.state.showPageList}
          margin={false}
          text="ページ　一覧"
          title="ページ一覧"
          ariaLabel="Pages" />
        <ToolIconButton
          onClick={() => this.handleToggleMenu('showHelp')}
          active={this.state.showHelp}
          margin={false}
          iconName="help"
          title="ヘルプ"
          ariaLabel="Help" />
        <ToolIconButton
          onClick={() => {
            if (reference !== undefined) { this.handleClickBookmarkButton(reference, bookmarkID); }
          }}
          disabled={reference === undefined}
          active={bookmarkID !== undefined}
          margin={false}
          iconName="bookmark"
          title="しおり"
          ariaLabel="Bookmark" />
        <ToolIconButton
          onClick={() => this.handleToggleMenu('showAppearancePopup')}
          active={this.state.showAppearancePopup}
          margin={false}
          iconName="appearance-setting"
          title="表示切替"
          ariaLabel="Appearance" />
        <ToolIconButton
          onClick={() => this.handleToggleMenu('showRemainingPopup')}
          active={this.state.showRemainingPopup}
          margin={false}
          iconName="setting"
          title="設定"
          ariaLabel="Setting" />
        {this.props.developMode
          ? <ToolIconButton
              onClick={() => this.handleToggleMenu('showDevPopup')}
              active={this.state.showDevPopup}
              margin={false}
              text="開発用"
              title="開発用"
              ariaLabel="Dev" />
          : null}
      </div>
      <ToolboxPopup visible={this.state.showPageList}>
        <PageList scale={0.75} rows={2} cols={3} />
      </ToolboxPopup>
      <ToolboxPopup visible={this.state.showAppearancePopup}>
        {this.createFeatureButtons()}
        <ToolIconButton
          onClick={this.handleToggleToolbarDirection}
          text="ツールバー回転"
          title="ツールバー回転" />
        <ToolIconButton
          active={this.props.drawingTool.showObjects}
          onClick={() => {
            const showObject = !this.props.drawingTool.showObjects;
            this.props.dispatch(DrawingToolAction.showObjects(showObject));

            const tool = this.props.ui.currentTool;
            if (!showObject && objectTools.indexOf(tool) !== -1) {
              UIAction.setCurrentTool(CurrentTool.Touch, this.props.dispatch);
            }
          }}
          iconName="appearance-drawings"
          title="書き込み表示"
          ariaLabel="Show Objects"
        />
        {developMode
          ? <ToolIconButton
              active={this.props.drawingTool.showLearningPenObjects}
              onClick={() => {
                const showPen = !this.props.drawingTool.showLearningPenObjects;
                this.props.dispatch(
                  DrawingToolAction.showLearningPenObjects(showPen),
                );

                if (!showPen && this.props.ui.currentTool === CurrentTool.LearningPen) {
                  UIAction.setCurrentTool(CurrentTool.Touch, this.props.dispatch);
                }
              }}
              text="暗記ペン表示"
              title="暗記ペン表示"
              ariaLabel="Show Learning Pen"
        /> : null}
        {developMode
          ? <ToolIconButton
              onClick={() => this.setState({ showBookmarksPopup: true })}
              iconName="appearance-bookmarks"
              title="しおり一覧"
              ariaLabel="Show bookmarks" />
          : null}
        {developMode
          ? <ToolIconButton
              iconName="appearance-markers"
              title="マーカー一覧"
              ariaLabel="Show markers" />
          : null}
        {developMode
          ? <ToolIconButton
              onClick={() => this.setState({ showMultiroleLinksPopup: true })}
              iconName="appearance-mylinks"
              title="マイリンク一覧"
              ariaLabel="Show multiroleLinks" />
          : null}
        {developMode
          ? <ToolIconButton
              iconName="appearance-histories"
              title="履歴一覧"
              ariaLabel="Show histories" />
          : null}
      </ToolboxPopup>
      <ToolboxPopup visible={this.state.showRemainingPopup}>
        <ToolIconButton
          onClick={() => this.setState({ showTransparencyPopup: true })}
          text="透明度設定"
          title="透明度設定"
          ariaLabel="Transparency" />
        {developMode
          ? <ToolIconButton
              onClick={this.props.onBackToBookshelf}
              iconName="remaining-bookshelf"
              title="本棚"
              ariaLabel="Bookshelf" />
          : null}
        {developMode
          ? <ToolIconButton
              onClick={() => this.setState({ showMiscSetting: true })}
              iconName="remaining-setting"
              title="各種設定"
              ariaLabel="Setting" />
          : null}
        <ToolIconButton
          onClick={() => this.setState({ showSystemInformation: true })}
          iconName="remaining-system-info"
          title="システム情報"
          ariaLabel="System information" />
        <ToolIconButton
          onClick={this.handleToggleFullscreen}
          active={this.state.isFullscreen}
          text="全画面"
          title="フルスクリーン" />
        {PLATFORM !== 'generic' ? null : <ToolIconButton
          onClick={this.props.onSaveWorkspace}
          text="学習履歴保存"
          title="学習履歴保存" />}

      </ToolboxPopup>
      <ToolboxPopup visible={this.state.showDevPopup}>
        <ToolIconButton
          onClick={this.copyPartialWorkspaceURL}
          text="URL生成"
          title="現在のページ位置を開くURLを生成" />
        <ToolIconButton
          onClick={() => this.handleImagePastingTest(1, 'large')}
          text="負荷 大x1"
          title="画像貼り付け負荷テスト(1920 x 1080 x 1)" />
        <ToolIconButton
          onClick={() => this.handleImagePastingTest(10, 'large')}
          text="負荷 大x10"
          title="画像貼り付け負荷テスト(1920 x 1080 x 10)" />
        <ToolIconButton
          onClick={() => this.handleImagePastingTest(10, 'small')}
          text="負荷 小x10"
          title="画像貼り付け負荷テスト(256 x 256 x 10)" />
        <ToolIconButton
          onClick={() => this.handleImagePastingTest(100, 'small')}
          text="負荷 小x100"
          title="画像貼り付け負荷テスト(256 x 256 x 100)" />
        <ToolIconButton
          onClick={() => this.handleObjectDrawingTest(10)}
          text="負荷 図形x10"
          title="図形描き込み負荷テスト(10)" />
        <ToolIconButton
          onClick={() => this.handleObjectDrawingTest(100)}
          text="負荷 図形x100"
          title="図形描き込み負荷テスト(100)" />
        <ToolIconButton
          onClick={() => this.props.dispatch(setLegacyUI(!legacyUI))}
          text={legacyUI ? '新UIへ' : '旧UIへ'}
          title="UI切り替え" />
      </ToolboxPopup>
      <TableOfContents
        isOpen={this.state.showTableOfContents}
        publication={publication}
        dispatch={this.props.dispatch}
        targetTab={this.props.pagesState.activeTab}
        onClose={() => this.setState({ showTableOfContents: false })} />
      <MiscSetting
        isOpen={this.state.showMiscSetting}
        onClose={() => this.setState({ showMiscSetting: false })} />
      <SystemInformation
        isOpen={this.state.showSystemInformation}
        onClose={() => this.setState({ showSystemInformation: false })} />
      <BookmarkItemListPopup
        userName={this.props.preference.userName}
        items={this.props.bookmarks}
        isOpen={this.state.showBookmarksPopup}
        dispatch={this.props.dispatch}
        publication={publication}
        onClickReference={reference => confirm('ページを いどう します。よろしいですか。')
          && this.goToReferenceIfIsEnabled(reference)}
        onClickItem={this.handleClickBookmarkListItem}
        onClickDeleteItem={item => this.props.dispatch(deleteBookmark(item.id))}
        onClose={() => this.setState({ showBookmarksPopup: false })} />
      <MultiroleLinkItemListPopup
        title={'マイリンク一覧'}
        items={this.props.multiroleLinks}
        isOpen={this.state.showMultiroleLinksPopup}
        onClick={this.handleClickMultiroleLinkListItem}
        onClickDelete={item => this.props.dispatch(removeMultiroleLink(item.id))}
        onClose={() => this.setState({ showMultiroleLinksPopup: false })} />
      {this.state.showTransparencyPopup
        ? <SettingPopup
          title={'透明度設定'}
          onClose={() => this.setState({ showTransparencyPopup: false })}>
          <ItemList
            items={trasparencyItems}
            onClick={this.handleClickTransparencyListItem} />
        </SettingPopup> : null}
      {this.state.showHelp && this.props.developMode && <HelpView />}
    </>;
  }
}

interface BottomToolbarProps {
  readonly onBackToBookshelf: () => void;
  readonly onSaveWorkspace: () => void;
  readonly onToggleFullScreen: () => void;
  readonly dispatch: Dispatch<Action>;
  readonly publication: Publication | undefined;
  readonly preference: PreferenceState;
  readonly pages: PagesState;
  readonly uimode: UIModeState;
  readonly bookmarks: ReadonlyArray<BookmarkInfo>;
  readonly multiroleLinks: MultiroleLinkInfo[];
  readonly legacyUI: boolean;
  readonly developMode: boolean;
  readonly drawingTool: DrawingToolState;
  readonly ui: UIState;
}

class BottomToolbar extends React.PureComponent<BottomToolbarProps> {
  public render() {
    const wrapperStyle: React.CSSProperties = {
      background: '#666',
      width: '100%',
      height: !this.props.legacyUI ? '50px' : '48px',
      display: 'flex',
      position: 'absolute',
      bottom: 0,
      zIndex: zIndex.bottomToolbar,
    };

    return (
      <div style={wrapperStyle}>
        {/* tabbar */}
        <div style={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
          {this.props.children}
        </div>

        <Toolbox
          onBackToBookshelf={this.props.onBackToBookshelf}
          onSaveWorkspace={this.props.onSaveWorkspace}
          onToggleFullScreen={this.props.onToggleFullScreen}
          contentFeatures={this.props.preference.contentFeatureFlags}
          dispatch={this.props.dispatch}
          publication={this.props.publication}
          pagesState={this.props.pages}
          preference={this.props.preference}
          uimode={this.props.uimode}
          bookmarks={this.props.bookmarks}
          multiroleLinks={this.props.multiroleLinks}
          developMode={this.props.developMode}
          legacyUI={this.props.legacyUI}
          drawingTool={this.props.drawingTool}
          ui={this.props.ui}
        />
      </div>
    );
  }
}

const mapStateToProps = (appStates: AppStates) => {
  return {
    drawingTool: appStates.drawingTool,
    publication: appStates.publication,
    pages: appStates.pages,
    preference: appStates.preference,
    uimode: appStates.uimode,
    bookmarks: appStates.bookmarks,
    legacyUI: appStates.legacyUI,
    developMode: appStates.developMode,
    ui: appStates.ui,
  };
};

export default connect(mapStateToProps)(BottomToolbar);
